import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { css } from "goober";
import { Listbox } from "@headlessui/react";

import { IS_DEV } from "@/__main__/constants.mjs";
import BlitzPortal from "@/util/BlitzPortal.jsx";
import { classNames } from "@/util/class-names.mjs";
import { useClientLayoutEffect } from "@/util/use-client-layout-effect.mjs";

import { mobile, mobileLarge, mobileMedium } from "../Style/style.mjs";
import { usePortalContainer } from "../util/use-portal-container.mjs";

export type SelectValue<T extends string | number> = {
  value: T;
  text: Translation;
  icon?: React.ReactNode;
  image?: string;
};
export interface SelectProps<T extends string | number> {
  options: readonly SelectValue<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  selected: T;
  containerClassName?: string;
  hideSelectedIcon?: boolean;
  listboxZIndex?: number;
  onClick?: () => void;
}

export function Select<T extends string | number>({
  options,
  onChange,
  disabled,
  selected = options?.[0]?.value,
  containerClassName,
  hideSelectedIcon,
  listboxZIndex,
  onClick,
}: SelectProps<T>): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);

  // Usage validation
  if (IS_DEV)
    // IS_DEV is constant, so this will always run in dev mode and thus is not conditional
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useMemo(() => {
      for (const option of options) {
        if (!option) continue;
        if (!Array.isArray(option.text))
          throw Error(
            `[Select] option.text must be Translation, not ${typeof option.text}. (In Select with options ${options
              .map((o) => o.value)
              .join(", ")})`,
          );
      }
    }, [options]);

  const selectedContent = options
    .filter(Boolean)
    .find((option) => selected === option.value);

  const portalContainer = usePortalContainer({
    fromElement: containerRef,
  });

  const selectButtonRef = useRef<HTMLButtonElement>(null);

  const isButtonVisible = useIsVisible(selectButtonRef);

  const optionsPosition = useCallback(
    (optionsNode: HTMLUListElement) => {
      if (optionsNode) {
        if (!portalContainer) return;
        if (!selectButtonRef.current) return;

        const portalRect = portalContainer.getBoundingClientRect();

        const selectButtonRect =
          selectButtonRef.current.getBoundingClientRect();

        const selectLeftCord =
          selectButtonRect.left - portalRect.left + portalContainer.scrollLeft;
        const selectRightCord =
          portalRect.right -
          selectButtonRect.right -
          portalContainer.scrollLeft;

        const selectYCord =
          selectButtonRect.y - portalRect.y + portalContainer.scrollTop;

        optionsNode.style.top = `${selectYCord}px`;

        if (selectLeftCord >= portalRect.width * 0.65) {
          optionsNode.style.left = "unset";
          optionsNode.style.right = `${selectRightCord}px`;
          optionsNode.style.transformOrigin = "top right";
        } else {
          optionsNode.style.right = "unset";
          optionsNode.style.left = `${selectLeftCord}px`;
          optionsNode.style.transformOrigin = "top left";
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [portalContainer],
  );

  const { t } = useTranslation();

  return (
    <div
      ref={containerRef}
      {...classNames(cssSelect(), containerClassName)}
      onClick={onClick}
    >
      <Listbox value={selected} onChange={onChange} disabled={disabled}>
        <Listbox.Button ref={selectButtonRef} {...classNames("selectButton")}>
          {selectedContent?.icon && !hideSelectedIcon && (
            <div className="icon">{selectedContent.icon}</div>
          )}
          {selectedContent?.image && (
            <div className="image">
              <img src={selectedContent.image} />
            </div>
          )}
          {selectedContent?.text && (
            <span className="type-callout--semi text">
              {t(...selectedContent.text)}
            </span>
          )}
          {disabled ? null : (
            <svg viewBox="0 0 16 16" className="caret">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4 6L8 10L12 6H4Z"
              />
            </svg>
          )}
        </Listbox.Button>

        <BlitzPortal
          fromElement={containerRef}
          style={{ zIndex: listboxZIndex }}
        >
          {isButtonVisible ? (
            <Listbox.Options
              className={`${cssSelectOptions()} options-container visible`}
              ref={optionsPosition}
            >
              {options.filter(Boolean).map((option) => (
                <Listbox.Option
                  key={option.value}
                  value={option.value}
                  className={({ active, selected }) =>
                    `${
                      active
                        ? "active-option option"
                        : selected
                          ? "selected-option option"
                          : "option"
                    }`
                  }
                >
                  {({ selected }) => (
                    <>
                      <div className="option--left">
                        {option?.icon ? (
                          <div className="option--icon">{option.icon}</div>
                        ) : option?.image ? (
                          <div className="option--image">
                            <img src={option.image} />
                          </div>
                        ) : null}
                        <span className="option--text type-form--button">
                          {t(...option.text)}
                        </span>
                      </div>
                      <svg
                        viewBox="0 0 32 32"
                        className={`option--check ${
                          selected ? "selected" : ""
                        }`}
                      >
                        <path d="M11.151 21.821l-6.363-6.269-2.121 2.090 8.485 8.358 18.182-17.911-2.121-2.089z" />
                      </svg>
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          ) : (
            ""
          )}
        </BlitzPortal>
      </Listbox>
    </div>
  );
}

function useIsVisible(ref: React.RefObject<HTMLElement>) {
  const [isIntersecting, setIntersecting] = useState(false);

  useClientLayoutEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(([entry]) =>
      setIntersecting(entry.isIntersecting),
    );

    observer.observe(ref.current);
    return () => {
      observer.disconnect();
    };
  }, [ref]);

  return isIntersecting;
}

export const cssSelect = () => css`
  --radii: var(--br, 5px);
  --max-chars: 24ch;
  position: relative;
  display: inline-block;

  ${mobile} {
    --max-chars: 20ch;
  }

  ${mobileLarge} {
    --max-chars: 16ch;
  }

  ${mobileMedium} {
    --max-chars: 12ch;
  }

  img {
    display: block;
  }

  .transition {
    transition:
      opacity 0.15s ease-in,
      transform 0.15s ease-in;
  }
  .showing {
    opacity: 1;
    transform: scale(1);
  }
  .hidden {
    opacity: 0;
    transform: scale(0.95);
  }

  .selectButton {
    --border: var(--shade6);
    --outline: transparent;
    --bg: var(--shade8);
    --svg: var(--shade2-75);
    position: relative;
    display: flex;
    align-items: center;
    padding-inline: var(--sp-3) var(--sp-10);
    height: var(--btn-height, 2.25rem);
    white-space: nowrap;
    background-color: var(--bg);
    border-radius: var(--br);
    border: 1px solid var(--border) !important;
    outline: 2px solid var(--outline);
    outline-offset: 2px;
    cursor: pointer;

    &:hover {
      --border: var(--shade3);
      --bg: var(--shade5-50);
    }

    &:focus {
      --outline: var(--shade1-50);
      z-index: 3;
    }

    .icon,
    .image {
      margin-right: var(--sp-2, 0.5rem);
    }
    .icon {
      height: var(--sp-4_5, 1.125rem);
      width: var(--sp-4_5, 1.125rem);

      img,
      svg {
        width: 100%;
        height: auto;
      }
    }
    .image {
      aspect-ratio: 1;

      img {
        width: var(--sp-5, 1.25rem);
        height: auto;
      }
    }

    .text {
      max-width: var(--max-chars);
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
    .caret {
      position: absolute;
      display: block;
      right: 0.75rem;
      top: 50%;
      height: 1rem;
      width: 1rem;
      fill: var(--shade0, white);
      transform: translateY(-50%);
    }
  }

  @keyframes enter {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
  }

  .select-close-trigger {
    display: none;
    position: fixed;
    inset: 0;
    z-index: 1;
  }
  &[data-show-options="true"] {
    .select-close-trigger {
      display: block;
    }
  }
`;

export const cssSelectOptions = () => css`
  --radii: var(--br, 5px);
  display: none;
  position: absolute;
  max-height: 70vh;
  max-width: fit-content;
  overflow-y: auto;
  padding: var(--sp-2);
  background: var(--shade10);
  border-radius: var(--radii);
  top: 0;
  left: 0;
  z-index: 11;
  animation: enter var(--transition);
  box-shadow:
    rgb(0 0 0 / 6%) 0px 3px 2px,
    rgb(0 0 0 / 10%) 0px 7px 5px,
    rgb(0 0 0 / 11%) 0px 12px 10px,
    rgb(0 0 0 / 13%) 0px 22px 18px,
    rgb(0 0 0 / 15%) 0px 42px 33px,
    rgb(0 0 0 / 21%) 0px 100px 80px;

  &.visible {
    display: block;
  }

  &.open-left {
    left: unset;
    right: 0;
    transform-origin: top right;
  }

  .option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 var(--sp-4);
    height: var(--sp-12);
    border-radius: var(--radii);
    transition: background var(--transition);
    cursor: pointer;

    &.selected-option {
      background: var(--shade8);
      box-shadow: var(--highlight);
    }
    &:hover,
    &.active-option {
      background: var(--shade6-50);
    }
  }

  .option--icon,
  .option--image {
    display: flex;
    height: var(--sp-8);
    margin-right: var(--sp-4);
    margin-left: calc(var(--sp-1) * -1);
  }
  .option--icon {
    svg {
      width: var(--sp-6);
      height: auto;
    }
  }
  .option--image {
    img {
      width: var(--sp-8);
      margin: auto;
      height: 100%;
    }
  }
  .option--left {
    display: flex;
    align-items: center;
  }
  .option--text {
    max-width: var(--max-chars);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .option--check {
    width: 1.5rem;
    height: 1.5rem;
    margin-left: var(--sp-12);
    opacity: 0;
    visibility: hidden;

    &.selected {
      opacity: 1;
      visibility: visible;
    }
  }
`;
